<template>
    <b-form :novalidate="validation" @submit.prevent ref="form">
        <component :is="hasTabs ? 'b-tabs' : 'div'">
            <component :is="hasTabs ? 'b-tab' : 'div'" v-for="(tab, index) in tabs" :title="tab.name" :key="`${index}-tab`" :style="hasTabs ? 'margin-top: 10px' : ''">
                <b-form-group
                    v-for="(item, index) in tab.fields"
                    :key="index"
                    :label="item.dynamicLabel ? item.dynamicLabel(formValues) : item.label"
                    v-if="!item.hide"
                    :description="item.dynamicDesc ? item.dynamicDesc(formValues) : item.desc"
                    :class="item.showIf && !item.showIf(formValues) ? 'd-none' : item.showIf && !item.noMarginOnShow ? 'ml-3' : ''"
                >
                    <b-form-input
                        v-if="item.type == 'text' || item.type == 'number' || !item.type"
                        :type="item.type"
                        v-model="formValues[item.key]"
                        :id="`input${item.key}${item.index}`"
                        :placeholder="item.placeholder"
                        :required="item.required ? '' : false"
                        :disabled="busy"
                        v-bind="item.props"
                    ></b-form-input>
                    <DatePicker
                        v-else-if="item.type == 'date'"
                        v-model="formValues[item.key]"
                        :id="`input${item.key}${item.index}`"
                        button-variant="outline-secondary"
                        :button-class="`btn-sm bg-light ${busy ? 'text-muted' : 'text-primary'}`"
                        :config="dateConfig"
                        :disabled="busy"
                    >
                        <i class="fa fa-fw fa-calendar-day"></i>
                        <span>{{ formValues[item.key] ? moment(formValues[item.key]).add(4, 'hours').format('LL') : trans('no_date') }}</span>
                        <i class="fa fa-fw fa-caret-down"></i>
                    </DatePicker>
                    <Datetime 
                        v-else-if="item.type == 'datetime'" 
                        v-model="formValues[item.key]" 
                        :id="`input${item.key}${item.index}`"
                        :config="dateOptions"
                        :disabled="busy"
                    >
                    </Datetime>
                    <b-form-textarea
                        v-else-if="item.type == 'textarea'"
                        v-model="formValues[item.key]"
                        :id="`input${item.key}${item.index}`"
                        :placeholder="item.placeholder"
                        :disabled="busy"
                        :required="item.required ? '' : false"
                    ></b-form-textarea>
                    <b-form-checkbox-group
                        v-else-if="item.type == 'checkboxgroup'"
                        v-model="formValues[item.key]"
                        :id="`input${item.key}${item.index}`"
                        :options="item.options"
                        :disabled="busy"
                    ></b-form-checkbox-group>
                    <b-form-select
                        v-else-if="item.type == 'select'"
                        v-model="formValues[item.key]"
                        :options="item.options"
                    ></b-form-select>
                    <b-form-checkbox
                        v-else-if="item.type == 'checkbox'"
                        v-model="formValues[item.key]"
                        :switch="item.switch"
                        v-bind="item.props"
                    >
                    {{ item.inside }}
                    </b-form-checkbox>
                    <DynamicSelect
                        v-else-if="item.type == 'category'"
                        v-model="formValues[item.key]"
                        :id="`input${item.key}${item.index}`"
                        :url="item.url"
                        :disabled="busy"
                    />
                    <b-form-radio-group
                        v-else-if="item.type == 'radioboxgroup'"
                        v-model="formValues[item.key]"
                        :id="`input${item.key}${item.index}`"
                        :options="item.options"
                        :disabled="busy"
                    ></b-form-radio-group>
                    <ModelTreeselect
                        v-else-if="item.type == 'tags' || item.type == 'treeselect'"
                        v-model="formValues[item.key]"
                        :url="item.url"
                        :id="`input${item.key}${item.index}`"
                        placeholder=''
                        :returnObject="item.returnObject"
                        :multiple="!item.single"
                    />
                    <FormParticipantFields
                        v-else-if="item.type == 'participant'"
                        v-model="formValues[item.key]"
                        :organization="item.organization"
                    />
                    <html-textarea
                        v-else-if="item.type == 'html'"
                        :organization="item.organization"
                        show-insert-form-document
                        v-model="formValues[item.key]"
                    ></html-textarea>
                    <DaysBefore 
                        v-else-if="item.type == 'daysbefore'"
                        v-model="formValues[item.key]"
                        :organization="item.organization"
                        :label="item.extraLabel"
                    />
                    <b-badge
                        v-else-if="item.type == 'badge'"
                        variant="primary"
                    >
                        {{ trans(formValues[item.key]) }}
                    </b-badge>
                    <div
                        v-else-if="item.type == 'content'"
                        v-html="item.content(formValues)"
                    >
                    </div>
                </b-form-group>
            </component>
        </component>
    </b-form>
</template>
<script>
import DatePicker from './DatePicker'
import DynamicSelect from './DynamicSelect'
import ModelTreeselect from './ModelTreeselect'
import FormParticipantFields from '../../frontend/components/form-questions/FormParticipantFields'
import DaysBefore from '../../frontend/components/automation/DaysBefore'
import Datetime from 'vue-bootstrap-datetimepicker'

export default {
    props: {
        fields: {
            type: Array,
            default: []
        },
        value: {
            type: Object,
            default() {
                return {};
            }
        },
        busy: {
            type: Boolean,
            default() {
                return false;
            },
        },
        validation: {
            type: Boolean,
            default() {
                return false;
            },
        },
    },
    components: {
        DatePicker,
        DynamicSelect,
        ModelTreeselect,
        FormParticipantFields,
        DaysBefore,
        Datetime,
    },
    data() {
        return {
            trans: trans,
            error: null,
            formValues: {},
            dateConfig: {
                locale: window.lang,
                format: 'YYYY-MM-DD',
                showTodayButton: true,
            },
            moment: moment,
            dateOptions: {
                locale: window.lang,
                format: 'LL',
                showClear: true,
            },
            tabs: this.getTabsFromFields()
        }
    },
    created() {
        this.formValues = this.value;
    },
    watch: {
        formValues() {
            this.$emit('input', this.formValues);
        },
        value() {
            this.formValues = this.value;
        },
    },
    methods: {
        getTabsFromFields(){
            let tabs = []
            let hasTabs = false
            let tab = null
            // could be better
            this.fields.map((f) => {
                if (f.tab) {
                    hasTabs = true

                    tab = tabs.find(function(ele) {
                        return ele.name === f.tab;
                    });

                    if (tab) {
                        tab.fields.push(f)
                    } else{
                        tabs.push({
                            name: f.tab,
                            fields: [f]
                        })
                    }
                }
            })

            if (!hasTabs) {
                tabs = [{
                    fields: this.fields
                }]
            }

            return tabs
        },
    },
    computed: {
        hasTabs(){
            if (this.fields.filter(e => e.tab != null).length > 0) {
                return true
            }

            return false
        },
    },
    mounted() {
        window.dynamicForm = this
    },
}
</script>
